/**
 * CanvasBackground — dynamic background layer rendered BEHIND ReactFlow.
 * Mounted inside `.canvas-container`, sits at z-index: 0; ReactFlow's
 * own elements get z-index: 1+ so nodes / dots / controls stay on top.
 *
 * Behavior:
 *   - kind === 'image' → CSS background-image with cover sizing
 *   - kind === 'video' → <video autoplay> that pauses on `ended` so it
 *     freezes on the last frame as a still backdrop.
 */
import { useEffect, useRef } from 'react';
import useCanvasBgStore from '../../store/canvasBgStore';

const CanvasBackground = () => {
  const { enabled, url, kind, opacity, blur } = useCanvasBgStore();
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || kind !== 'video' || !url) return;
    // Reset playback when video source changes
    el.currentTime = 0;
    el.play().catch(() => { /* autoplay blocked - that's OK, last frame still shows once user interacts */ });
  }, [url, kind]);

  if (!enabled || !url) return null;

  const filterStyle = blur > 0 ? `blur(${blur}px)` : 'none';

  return (
    <div
      className="canvas-bg-layer"
      data-testid="canvas-bg-layer"
      style={{
        opacity,
        filter: filterStyle,
      }}
    >
      {kind === 'video' ? (
        <video
          ref={videoRef}
          src={url}
          muted
          playsInline
          preload="auto"
          loop
          className="canvas-bg-media"
          data-testid="canvas-bg-video"
        />
      ) : (
        <img
          src={url}
          alt=""
          className="canvas-bg-media"
          data-testid="canvas-bg-image"
        />
      )}
      {/* Subtle dark gradient on top so node UI keeps readable */}
      <div className="canvas-bg-veil" />
    </div>
  );
};

export default CanvasBackground;
